import React, { useState, useEffect } from 'react';
import LoadingScreen from './LoadingScreen.jsx';

/**
 * Higher-Order Component (HOC) that wraps pages with a loading screen
 * @param {React.Component} WrappedComponent - The component to wrap
 * @param {Object} options - Configuration options
 * @param {number} options.minLoadingTime - Minimum loading time in milliseconds (default: 2000)
 * @param {number} options.maxLoadingTime - Maximum loading time in milliseconds (default: 4000)
 * @returns {React.Component} - Component with loading screen
 */
export default function withLoadingScreen(WrappedComponent, options = {}) {
  const {
    minLoadingTime = 2000,
    maxLoadingTime = 4000
  } = options;

  return function LoadingScreenWrapper(props) {
    const [isLoading, setIsLoading] = useState(true);
    const [componentReady, setComponentReady] = useState(false);

    useEffect(() => {
      // Simulate loading time with some randomness for realism
      const loadingTime = minLoadingTime + Math.random() * (maxLoadingTime - minLoadingTime);
      
      const timer = setTimeout(() => {
        setComponentReady(true);
      }, loadingTime);

      return () => clearTimeout(timer);
    }, []);

    const handleLoadingComplete = () => {
      setIsLoading(false);
    };

    return (
      <>
        <LoadingScreen 
          isLoading={isLoading && !componentReady} 
          onComplete={handleLoadingComplete}
        />
        {componentReady && (
          <WrappedComponent {...props} />
        )}
      </>
    );
  };
}

/**
 * Hook for manual loading control
 * Useful for pages that need to load data before showing content
 */
export function usePageLoading(initialLoading = true) {
  const [isLoading, setIsLoading] = useState(initialLoading);
  const [progress, setProgress] = useState(0);

  const startLoading = () => {
    setIsLoading(true);
    setProgress(0);
  };

  const updateProgress = (newProgress) => {
    setProgress(Math.min(100, Math.max(0, newProgress)));
  };

  const finishLoading = () => {
    setProgress(100);
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  return {
    isLoading,
    progress,
    startLoading,
    updateProgress,
    finishLoading,
    setIsLoading
  };
}